/**
 * Phase 306 — Referral Join Landing
 *
 * Public route at /join/:academy_id?ref=<code>
 *
 * Flow:
 *  1. Parse `?ref` and academy_id from URL.
 *  2. Resolve the referral code via GET /api/referrals/lookup/{code}
 *     (soft-fail: if 404 or wrong-academy, just drop the code and
 *     continue onboarding — never wall a warm lead).
 *  3. Show "Referred by Aarav at VBFC! Get ₹500 off your first month"
 *     hero + a phone input.
 *  4. Submit → POST /api/referrals/start-onboarding which fires the
 *     WhatsApp magic link.
 *  5. Toast "Check your WhatsApp — we sent you a link to finish
 *     registering" and route the user to a thank-you screen.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Sparkles, MessageCircle, CheckCircle2, Trophy, Gift, ShieldCheck, Loader2,
} from 'lucide-react';

const COPY = {
  hero_with_ref_title: 'You just got ₹500 off your first month! 🎉',
  hero_no_ref_title: 'Join the academy — register in 30 seconds',
  hero_with_ref_sub: (refName, academyName) =>
    `${refName || 'A friend'} from ${academyName || 'the academy'} referred you. Get ₹500 off your first month, and they get ₹500 off their next bill!`,
  hero_no_ref_sub: 'Drop your phone number — our coach will text you on WhatsApp to finish enrolment.',
  cta_label: 'Send me the WhatsApp link',
  thanks_title: 'Check your WhatsApp 📱',
  thanks_sub: 'We just sent a registration link to your phone. Open the WhatsApp message to finish signing up.',
  soft_fail_toast: "We couldn't verify that referral code, but let's get you signed up anyway!",
};

const PERKS = [
  { icon: Gift, title: '₹500 instant credit', body: 'Applied automatically on your first month\'s fees.' },
  { icon: ShieldCheck, title: 'Pay later, no card needed', body: "We won't ask for payment until after the trial session." },
  { icon: Trophy, title: '24-hour link', body: 'Your registration WhatsApp link stays active for a full day.' },
];

function _normalizePhone(v) {
  return (v || '').replace(/\D/g, '').slice(-10);
}

function HeroBadge({ refName, academyName, softFail }) {
  if (softFail) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium" data-testid="ref-soft-fail-badge">
        <ShieldCheck className="w-3.5 h-3.5" />
        Continuing without referral code &mdash; that&apos;s OK!
      </div>
    );
  }
  if (!refName) return null;
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium" data-testid="ref-resolved-badge">
      <Sparkles className="w-3.5 h-3.5" />
      Referred by <strong className="mx-0.5">{refName}</strong> at {academyName}
    </div>
  );
}

export default function ReferralJoinLanding() {
  const { academy_id: academyId } = useParams();
  const [params] = useSearchParams();
  const refCode = (params.get('ref') || '').toUpperCase();

  const [resolveStatus, setResolveStatus] = useState('loading'); // loading | resolved | soft_fail | no_code
  const [referrer, setReferrer] = useState(null);
  const [academyName, setAcademyName] = useState('');
  const [phone, setPhone] = useState('');
  const [parentName, setParentName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Resolve the referral code on mount. SOFT-FAIL on any error.
  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      if (!refCode) {
        setResolveStatus('no_code');
        return;
      }
      try {
        const { data } = await api.get(`/referrals/lookup/${refCode}`);
        if (cancelled) return;
        if (data && data.academy_id === academyId) {
          setReferrer(data);
          setAcademyName(data.academy_name || '');
          setResolveStatus('resolved');
        } else {
          // Code resolved but to a DIFFERENT academy → soft-fail.
          setResolveStatus('soft_fail');
        }
      } catch (_err) {
        if (cancelled) return;
        setResolveStatus('soft_fail');
      }
    }
    resolve();
    return () => { cancelled = true; };
  }, [refCode, academyId]);

  const phoneClean = useMemo(() => _normalizePhone(phone), [phone]);
  const canSubmit = phoneClean.length === 10 && !submitting;

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/referrals/start-onboarding', {
        academy_id: academyId,
        phone: phoneClean,
        ref: refCode || null,
        parent_name: parentName.trim() || null,
      });
      if (data?.soft_fail && refCode) {
        toast.message(COPY.soft_fail_toast);
      }
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404) {
        toast.error("We couldn't find that academy. Please check the link and try again.");
      } else if (status === 403 && detail?.code === 'FEATURE_DISABLED') {
        toast.error("This academy hasn't enabled WhatsApp onboarding yet. Please call them directly.");
      } else {
        toast.error("Couldn't send the WhatsApp link. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Done state ──────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4" data-testid="referral-join-done">
        <Card className="max-w-md w-full border-emerald-200 shadow-2xl">
          <CardContent className="pt-10 pb-8 px-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{COPY.thanks_title}</h1>
            <p className="text-slate-600 text-sm mb-6">{COPY.thanks_sub}</p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-left">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">What happens next?</p>
              <ol className="text-sm text-slate-700 space-y-1.5 list-decimal list-inside">
                <li>Open the WhatsApp message we just sent.</li>
                <li>Tap the registration link.</li>
                <li>Answer 4 quick questions — name, DOB, sport, contact.</li>
                <li>Done! A coach will reach out to schedule your trial.</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Loading state (only briefly — soft-fail kicks in fast) ───────
  if (resolveStatus === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center" data-testid="referral-join-loading">
        <Loader2 className="w-7 h-7 text-emerald-600 animate-spin" />
      </div>
    );
  }

  const showRefHero = resolveStatus === 'resolved' && referrer;
  const heroTitle = showRefHero ? COPY.hero_with_ref_title : COPY.hero_no_ref_title;
  const heroSub = showRefHero
    ? COPY.hero_with_ref_sub(referrer?.referrer_student_name, referrer?.academy_name)
    : COPY.hero_no_ref_sub;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4" data-testid="referral-join-landing">
      <div className="max-w-md w-full">
        {/* Brand strip */}
        <div className="flex items-center justify-center gap-2 mb-6 text-slate-500 text-xs uppercase tracking-widest font-semibold">
          <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
          Out-Play Academy Network
        </div>

        <Card className="border-emerald-200/60 shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-7 py-6 text-white">
            <div className="mb-3">
              <HeroBadge
                refName={referrer?.referrer_student_name}
                academyName={referrer?.academy_name}
                softFail={resolveStatus === 'soft_fail'}
              />
            </div>
            <h1 className="text-2xl font-bold leading-tight" data-testid="referral-hero-title">{heroTitle}</h1>
            <p className="text-emerald-50 text-sm mt-2 leading-relaxed" data-testid="referral-hero-sub">{heroSub}</p>
          </div>

          <CardContent className="pt-7 pb-6 px-7">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="parentName" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Parent&apos;s name (optional)</label>
                <Input
                  id="parentName"
                  type="text"
                  value={parentName}
                  onChange={(e) => setParentName(e.target.value)}
                  placeholder="e.g., Priya Sharma"
                  className="mt-1.5 h-11"
                  data-testid="referral-parent-name-input"
                />
              </div>
              <div>
                <label htmlFor="phone" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">WhatsApp number*</label>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="px-3 h-11 flex items-center bg-slate-100 border border-slate-200 rounded-md text-slate-600 font-medium text-sm">+91</span>
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="10-digit number"
                    maxLength={14}
                    className="flex-1 h-11 text-lg tracking-wide tabular-nums"
                    required
                    data-testid="referral-phone-input"
                  />
                </div>
                {phone && phoneClean.length !== 10 ? (
                  <p className="text-xs text-rose-600 mt-1.5" data-testid="phone-error">Please enter a valid 10-digit number</p>
                ) : null}
              </div>

              <Button
                type="submit"
                disabled={!canSubmit}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="referral-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <><MessageCircle className="w-4 h-4 mr-2" /> {COPY.cta_label}</>
                )}
              </Button>
            </form>

            {/* Trust signals */}
            <div className="mt-7 pt-6 border-t border-slate-100 grid grid-cols-1 gap-3">
              {PERKS.map((p, idx) => (
                <div key={idx} className="flex items-start gap-3" data-testid={`referral-perk-${idx}`}>
                  <div className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                    <p.icon className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{p.title}</p>
                    <p className="text-xs text-slate-500">{p.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-5">
          By submitting, you agree to receive a one-time registration message on WhatsApp.
        </p>
      </div>
    </div>
  );
}
